import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Heart, MessageCircle, User as UserIcon, LogOut, Crown, BadgeCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationsContext";
import { api } from "../lib/api";

const NAV_ITEMS = [
  { to: "/discover", label: "Discover", icon: Sparkles, testId: "nav-discover" },
  { to: "/matches", label: "Matches", icon: Heart, testId: "nav-matches" },
  { to: "/messages", label: "Messages", icon: MessageCircle, testId: "nav-messages", badge: true },
  { to: "/profile/edit", label: "Profile", icon: UserIcon, testId: "nav-profile" },
];

function Badge({ count }) {
  if (!count) return null;
  return (
    <span
      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-[#050505] text-[10px] font-medium flex items-center justify-center"
      data-testid="unread-badge"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { unread } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  const isActive = (to) => {
    if (to === "/profile/edit") return location.pathname.startsWith("/profile");
    if (to === "/messages") return location.pathname.startsWith("/messages");
    return location.pathname === to;
  };

  const isPremium = user?.subscription_tier === "premium";
  const isAdmin = user?.role === "admin";
  const emailUnverified = user && user.email_verified === false;

  const resendVerification = async () => {
    try {
      await api.post("/auth/email/send-verification");
      toast.success("Verification email sent. Check your inbox.");
    } catch {
      toast.error("Couldn't send. Try again in a moment.");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="app-layout">
      {/* Top nav (desktop) */}
      <header className="hidden md:block sticky top-0 z-40 glass-strong">
        <div className="max-w-6xl mx-auto px-8 h-20 flex items-center justify-between">
          <Link to="/discover" className="font-display text-2xl tracking-tight text-gold flex items-center gap-2">
            Wanted
            {isPremium && <span className="text-[10px] tracking-overline text-gold-soft border border-[#D4AF37]/40 px-2 py-0.5 rounded-full">PREMIUM</span>}
          </Link>
          <div className="flex items-center gap-2">
            {NAV_ITEMS.map((it) => {
              const Icon = it.icon;
              const active = isActive(it.to);
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  className={`relative px-5 py-2.5 rounded-full text-sm transition-all flex items-center gap-2 ${
                    active ? "bg-[#D4AF37]/15 text-gold border border-[#D4AF37]/30" : "text-white/60 hover:text-white"
                  }`}
                  data-testid={it.testId}
                >
                  <span className="relative">
                    <Icon size={16} />
                    {it.badge && <Badge count={unread} />}
                  </span>
                  {it.label}
                </Link>
              );
            })}
            {!isPremium && (
              <Link
                to="/premium"
                className={`ml-2 px-5 py-2.5 rounded-full text-sm flex items-center gap-2 transition-all ${
                  location.pathname === "/premium"
                    ? "bg-[#D4AF37] text-[#050505]"
                    : "border border-[#D4AF37]/50 text-gold hover:bg-[#D4AF37]/10"
                }`}
                data-testid="nav-premium"
              >
                <Crown size={14} /> Premium
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                className={`ml-2 px-4 py-2.5 rounded-full text-sm flex items-center gap-2 transition-all ${
                  location.pathname === "/admin"
                    ? "bg-white/10 text-white"
                    : "text-white/50 hover:text-white"
                }`}
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm flex items-center justify-end gap-1.5">
                {user?.name}
                {user?.verification_status === "verified" && (
                  <BadgeCheck size={14} className="text-gold" fill="rgba(212,175,55,0.25)" data-testid="me-verified-badge" />
                )}
              </div>
              <div className="text-xs text-white/40 tracking-wide">{user?.email}</div>
            </div>
            <button
              onClick={handleLogout}
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center text-white/60 hover:text-gold hover:border-[#D4AF37]/40 transition-colors"
              title="Sign out"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile header */}
      <header className="md:hidden sticky top-0 z-40 glass-strong">
        <div className="px-6 h-16 flex items-center justify-between">
          <Link to="/discover" className="font-display text-2xl tracking-tight text-gold flex items-center gap-2">
            Wanted
            {isPremium && <Crown size={14} className="text-gold-soft" />}
          </Link>
          <div className="flex items-center gap-3">
            {!isPremium && (
              <Link to="/premium" className="text-gold" data-testid="nav-premium-mobile">
                <Crown size={18} />
              </Link>
            )}
            <button onClick={handleLogout} className="text-white/60" data-testid="logout-btn-mobile">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 pb-24 md:pb-12">
        {emailUnverified && (
          <div className="border-b border-[#D4AF37]/15 bg-[#D4AF37]/[0.03]" data-testid="email-verify-banner">
            <div className="max-w-6xl mx-auto px-6 py-2.5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs">
              <span className="text-white/55 flex items-center gap-2">
                <ShieldCheck size={12} className="text-gold-soft" />
                A quiet note: please confirm your email address.
              </span>
              <button
                onClick={resendVerification}
                className="text-gold hover:text-gold-soft tracking-wide"
                data-testid="resend-verify-btn"
              >
                Resend link →
              </button>
            </div>
          </div>
        )}
        <Outlet />
      </main>

      {/* Quiet legal footer — visible on every in-app page */}
      <div className="border-t border-white/5 py-6 md:py-8 px-6 md:px-8" data-testid="app-legal-footer">
        <nav className="max-w-6xl mx-auto flex flex-wrap items-center justify-center md:justify-between gap-x-6 gap-y-3 text-[11px] tracking-wide text-white/35">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link to="/terms" className="hover:text-white/70 transition-colors" data-testid="app-footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-white/70 transition-colors" data-testid="app-footer-privacy">Privacy</Link>
            <Link to="/refunds" className="hover:text-white/70 transition-colors" data-testid="app-footer-refunds">Refunds</Link>
            <Link to="/community-guidelines" className="hover:text-white/70 transition-colors" data-testid="app-footer-community">Community</Link>
          </div>
          <div className="text-white/25 tracking-overline">© {new Date().getFullYear()} WANTED</div>
        </nav>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass-strong border-t border-white/10">
        <div className="grid grid-cols-4">
          {NAV_ITEMS.map((it) => {
            const Icon = it.icon;
            const active = isActive(it.to);
            return (
              <Link
                key={it.to}
                to={it.to}
                className={`flex flex-col items-center justify-center py-3 gap-1 ${active ? "text-gold" : "text-white/50"}`}
                data-testid={`${it.testId}-mobile`}
              >
                <span className="relative">
                  <Icon size={20} />
                  {it.badge && <Badge count={unread} />}
                </span>
                <span className="text-[10px] tracking-wide">{it.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
